import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { port } from '../../App'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import InputFieldform from '../SettingComponent/InputFieldform'

const AddBillingCycleModal = ({ show, setshow, client_id, onSuccess }) => {
    const [placements, setPlacements] = useState([])
    const [formData, setFormData] = useState({
        joining_details: '',
        cycle_month: 1,
        billing_amount: '',
        due_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        is_contract_basis: false,
        candidate_payout_amount: ''
    })
    const [loading, setLoading] = useState(false)
    const [nextMonthHint, setNextMonthHint] = useState(null) // e.g. "Next month: 3"

    // Fetch all placements for this client when modal opens
    useEffect(() => {
        if (show && client_id) {
            axios.get(`${port}/root/cms/client-joining/?client_id=${client_id}`)
                .then(res => {
                    setPlacements(res.data)
                    if (res.data.length > 0) {
                        const first = res.data[0]
                        setFormData(prev => ({
                            ...prev,
                            joining_details: first.id,
                            billing_amount: first.CTC || '',
                            is_contract_basis: first.is_contract_basis || false,
                            candidate_payout_amount: first.candidate_salary && parseFloat(first.candidate_salary) > 0 ? parseFloat(first.candidate_salary) : ''
                        }))
                        // Auto-fetch next available month for the first placement
                        fetchNextMonth(first.id)
                    }
                })
                .catch(err => {
                    console.error("Error fetching placements:", err)
                    toast.error("Could not load placements.")
                })
        }
    }, [show, client_id])

    // When modal is hidden, reset form
    useEffect(() => {
        if (!show) {
            setFormData({
                joining_details: '',
                cycle_month: 1,
                billing_amount: '',
                due_date: new Date().toISOString().split('T')[0],
                status: 'pending',
                is_contract_basis: false,
                candidate_payout_amount: ''
            })
            setNextMonthHint(null)
        }
    }, [show])

    // Fetch existing cycles for the selected joining and find the next month
    const fetchNextMonth = (joiningId) => {
        if (!joiningId) return
        // Virtual placements (virtual_hrfs_xxx) have no real joining record yet —
        // they can't have existing billing cycles, so always start at Month 1
        if (String(joiningId).startsWith('virtual_')) {
            setFormData(prev => ({ ...prev, cycle_month: 1 }))
            setNextMonthHint(null)
            return
        }
        axios.get(`${port}/root/cms/billing-cycles/?joining_id=${joiningId}`)
            .then(res => {
                const existingCycles = res.data
                if (existingCycles.length > 0) {
                    const maxMonth = Math.max(...existingCycles.map(c => c.cycle_month))
                    const nextMonth = maxMonth + 1
                    setFormData(prev => ({ ...prev, cycle_month: nextMonth }))
                    setNextMonthHint(`Month 1–${maxMonth} already created. Auto-set to Month ${nextMonth}.`)
                } else {
                    setFormData(prev => ({ ...prev, cycle_month: 1 }))
                    setNextMonthHint(null)
                }
            })
            .catch(() => {
                // silently ignore, default to 1
            })
    }

    // When user changes placement, update amount and auto-set the next month
    const handlePlacementChange = (e) => {
        const selectedId = e.target.value
        const sel = placements.find(p => String(p.id) === String(selectedId))
        setFormData(prev => ({
            ...prev,
            joining_details: selectedId,
            billing_amount: sel ? (sel.CTC || '') : '',
            is_contract_basis: sel ? (sel.is_contract_basis || false) : false,
            candidate_payout_amount: sel && sel.candidate_salary && parseFloat(sel.candidate_salary) > 0 ? parseFloat(sel.candidate_salary) : ''
        }))
        setNextMonthHint(null)
        fetchNextMonth(selectedId)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (loading) return // Prevent double-clicks
        if (formData.is_contract_basis && (!formData.candidate_payout_amount || parseFloat(formData.candidate_payout_amount) <= 0)) {
            toast.error("Please enter a valid candidate payout amount for contract placements.")
            return
        }
        if (formData.is_contract_basis && parseFloat(formData.candidate_payout_amount) > parseFloat(formData.billing_amount)) {
            toast.error("Candidate salary cannot exceed the billed amount to the client.")
            return
        }

        setLoading(true)
        try {
            await axios.post(`${port}/root/cms/billing-cycles/`, formData)
            toast.success(`Billing cycle for Month ${formData.cycle_month} created successfully!`)
            setshow(false)
            if (onSuccess) onSuccess()
        } catch (error) {
            const errData = error?.response?.data
            const errMsg = errData?.error || (errData?.non_field_errors ? errData.non_field_errors[0] : "")
            
            // If the error says it already exists, the first click likely worked.
            // Close the modal and refresh the table instead of staying stuck.
            if (errMsg && (errMsg.includes("already exists") || errMsg.includes("unique set"))) {
                toast.success(`Billing cycle for Month ${formData.cycle_month} is already available.`)
                setshow(false)
                if (onSuccess) onSuccess()
                return
            }

            if (errData) {
                if (errData.error) {
                    toast.error(errData.error)
                } else {
                    const firstKey = Object.keys(errData)[0]
                    toast.error(`${firstKey}: ${errData[firstKey]}`)
                }
            } else {
                toast.error("Failed to create billing cycle. Please try again.")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    return (
        <Modal show={show} onHide={() => setshow(false)} centered size="md">
            <Modal.Header closeButton>
                <Modal.Title className="text-sm font-bold uppercase">Add New Billing Cycle</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Placement Dropdown */}
                    <div>
                        <InputFieldform 
                            size="w-full"
                            value={formData.joining_details} 
                            name="joining_details" 
                            label="Select Placement (Candidate)"
                            optionObj={placements.map(p => ({
                                label: `${p.candidate_details?.FirstName} - ${p.requirement_details?.job_title}`,
                                value: p.id
                            }))}
                            handleChange={handlePlacementChange} 
                        />
                    </div>

                    {/* Cycle Month + Amount */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <InputFieldform
                                size="w-full"
                                value={formData.cycle_month}
                                name="cycle_month"
                                label="Cycle Month #"
                                type="number"
                                min="1"
                                handleChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <InputFieldform
                                size="w-full"
                                value={formData.billing_amount}
                                name="billing_amount"
                                label="Billing Amount (₹)"
                                type="number"
                                handleChange={handleInputChange}
                            />
                        </div>
                    </div>

                    {/* Hint about next available month */}
                    {nextMonthHint && (
                        <p className="text-[11px] text-blue-600 font-semibold mb-2">ℹ️ {nextMonthHint}</p>
                    )}

                    {/* Phase 2: Contract Info Toggle */}
                    <div className="bg-slate-50 p-3 rounded border border-slate-100">
                        <label className="flex items-center space-x-2 cursor-pointer mb-2">
                            <input
                                type="checkbox"
                                name="is_contract_basis"
                                checked={formData.is_contract_basis}
                                onChange={handleInputChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Is Contract Basis?</span>
                        </label>
                        
                        {formData.is_contract_basis && (
                            <div className="mt-3">
                                <InputFieldform
                                    size="w-full"
                                    value={formData.candidate_payout_amount}
                                    name="candidate_payout_amount"
                                    label="Candidate Salary (Payout ₹)"
                                    type="number"
                                    handleChange={handleInputChange}
                                />
                                <p className="text-[10px] text-gray-400 mt-1 italic">
                                    The candidate's monthly payout. Net Margin will be (Billed Amount - Payout).
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Due Date */}
                    <div>
                        <InputFieldform
                            size="w-full"
                            value={formData.due_date}
                            name="due_date"
                            label="Due Date"
                            type="date"
                            handleChange={handleInputChange}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => setshow(false)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm text-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Cycle'}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    )
}

export default AddBillingCycleModal
